/**
 * F-148 §10.2 Review: `now → +accepted` per variant plus totals (AC13). Product and variant
 * names render in `<bdi>`; numbers render LTR.
 */
import { useTranslation } from '@dukanti/shared';
import type { ReceivingPreview } from '@dukanti/shared/admin';
import { cn } from '@/lib/utils';

export interface ReviewDeltaListProps {
  readonly deltas: ReceivingPreview['deltas'];
  readonly totals: ReceivingPreview['totals'];
  readonly className?: string;
}

export function ReviewDeltaList({
  deltas,
  totals,
  className,
}: ReviewDeltaListProps): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <div className={cn('space-y-4 text-start', className)} data-testid="review-delta-list">
      <ul className="border-border bg-card shadow-admin-card divide-border divide-y overflow-hidden rounded-lg border">
        {deltas.map((delta) => (
          <li
            key={delta.variantId}
            className="grid grid-cols-[1fr_auto] items-center gap-3 px-4 py-3"
            data-testid="review-delta"
            data-variant-id={delta.variantId}
          >
            <div className="min-w-0">
              <p className="text-foreground truncate text-sm font-medium">
                <bdi>{delta.productName}</bdi>
              </p>
              {delta.optionSummary ? (
                <p className="text-muted-foreground truncate text-xs">
                  <bdi>{delta.optionSummary}</bdi>
                </p>
              ) : null}
            </div>
            <div className="flex items-baseline gap-2 text-sm">
              <span className="text-muted-foreground text-xs">
                {t('admin.receiving.review.inventoryNow')}
              </span>
              <bdi
                dir="ltr"
                className="text-foreground tabular-nums"
                data-testid="review-inventory-now"
              >
                {delta.inventoryNow}
              </bdi>
              <span aria-hidden="true" className="text-muted-foreground rtl:rotate-180">
                &rarr;
              </span>
              <bdi
                dir="ltr"
                className="text-admin-lime-hover font-semibold tabular-nums"
                data-testid="review-delta-adds"
              >
                {t('admin.receiving.review.adds', { count: delta.acceptedUnits })}
              </bdi>
              <span className="sr-only">
                {t('admin.receiving.review.after')} {delta.inventoryNow + delta.acceptedUnits}
              </span>
            </div>
          </li>
        ))}
      </ul>

      <dl
        className="border-border bg-admin-surface-muted grid grid-cols-2 gap-3 rounded-lg border p-4 text-sm sm:grid-cols-5"
        data-testid="review-totals"
        aria-label={t('admin.receiving.review.totals')}
      >
        <div>
          <dt className="text-muted-foreground text-xs">{t('admin.receiving.draft.lines')}</dt>
          <dd className="text-foreground font-semibold tabular-nums">
            <bdi dir="ltr">{totals.lines}</bdi>
          </dd>
        </div>
        <div>
          <dt className="text-muted-foreground text-xs">{t('admin.receiving.line.expected')}</dt>
          <dd className="text-foreground font-semibold tabular-nums">
            <bdi dir="ltr">{totals.expected}</bdi>
          </dd>
        </div>
        <div>
          <dt className="text-muted-foreground text-xs">{t('admin.receiving.line.accepted')}</dt>
          <dd
            className="text-foreground font-semibold tabular-nums"
            data-testid="review-total-accepted"
          >
            <bdi dir="ltr">{totals.accepted}</bdi>
          </dd>
        </div>
        <div>
          <dt className="text-muted-foreground text-xs">{t('admin.receiving.line.damaged')}</dt>
          <dd className="text-foreground font-semibold tabular-nums">
            <bdi dir="ltr">{totals.damaged}</bdi>
          </dd>
        </div>
        <div>
          <dt className="text-muted-foreground text-xs">{t('admin.receiving.line.missing')}</dt>
          <dd className="text-foreground font-semibold tabular-nums">
            <bdi dir="ltr">{totals.missing}</bdi>
          </dd>
        </div>
      </dl>
    </div>
  );
}
